import classnames from "classnames";
import { ComponentPropsWithRef, PropsWithChildren, useContext } from "react";
import AsyncButton from "../AsyncButton/AsyncButton";

import Button from "../../base/buttons/Button/Button";
import Modal, { ModalContext } from "../../base/dialogs/Modal/Modal";
import Text from "../../base/type/Text/Text";
import styles from "./AlertModal.module.scss";

type AlertModalWrapperProps = PropsWithChildren<{
  readonly variant?: "positive" | "negative";
  readonly title?: string;
  readonly text?: string;
}>;
export function AlertModalWrapper({
  variant,
  title,
  text,
  children,
}: AlertModalWrapperProps) {
  return (
    <div
      data-testid="alert-modal-container"
      className={classnames(
        "flex w-[380px] flex-col items-center justify-center border-t-4 bg-pure-white p-[30px] text-center",
        {
          "border-straps-positive": variant === "positive",
          "border-straps-negative": variant === "negative",
        }
      )}
    >
      {!!title && (
        <Text
          variant="h3_t-24-700"
          className={classnames("mb-[15px]", styles.modalTitle)}
        >
          {title}
        </Text>
      )}
      {!!text && (
        <Text variant="sb_t-18-500" className="mb-[15px] whitespace-pre-line">
          {text}
        </Text>
      )}
      {children}
    </div>
  );
}

interface AlertModalContentProps extends AlertModalWrapperProps {
  readonly loading?: boolean;
  readonly primaryButtonTitle?: string;
  readonly secondaryButtonTitle?: string;
  readonly onPressPrimaryButton?: () => Promise<any> | void;
  readonly onPressSecondaryButton?: () => void;
  readonly closeOnButtonPress?: boolean;
}
export const AlertModalContent = ({
  loading,
  variant,
  title,
  text,
  primaryButtonTitle,
  secondaryButtonTitle,
  onPressPrimaryButton,
  onPressSecondaryButton,
  closeOnButtonPress,
  children,
}: AlertModalContentProps) => {
  const { setOpen } = useContext(ModalContext);

  return (
    <AlertModalWrapper variant={variant} title={title} text={text}>
      {children}
      <div className="mt-[15px] flex items-center">
        {!!secondaryButtonTitle && (
          <Button
            dataTestId="alert-modal-secondary-button"
            noFrameVariant="text"
            className="mr-7"
            disabled={loading}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onPressSecondaryButton?.();
              if (!onPressSecondaryButton || closeOnButtonPress) {
                setOpen(false);
              }
            }}
          >
            {secondaryButtonTitle}
          </Button>
        )}
        <AsyncButton
          dataTestId="alert-modal-primary-button"
          size="large"
          className="min-w-[140px]"
          color={variant === "negative" ? "negative" : "positive"}
          onClick={async () => {
            await onPressPrimaryButton?.();
            if (!onPressPrimaryButton || closeOnButtonPress) {
              setOpen(false);
            }
          }}
        >
          {primaryButtonTitle || "Close"}
        </AsyncButton>
      </div>
    </AlertModalWrapper>
  );
};

type AlertModalProps = Omit<ComponentPropsWithRef<typeof Modal>, "children"> &
  AlertModalContentProps;

const AlertModal = ({
  loading,
  variant = "positive",
  title,
  text,
  primaryButtonTitle,
  secondaryButtonTitle,
  onPressPrimaryButton,
  onPressSecondaryButton,
  closeOnButtonPress,
  children,
  ...modalProps
}: AlertModalProps) => {
  return (
    <Modal data-testid={"alert-modal"} {...modalProps}>
      <AlertModalContent
        loading={loading}
        variant={variant}
        title={title}
        text={text}
        primaryButtonTitle={primaryButtonTitle}
        secondaryButtonTitle={secondaryButtonTitle}
        onPressPrimaryButton={onPressPrimaryButton}
        onPressSecondaryButton={onPressSecondaryButton}
        closeOnButtonPress={closeOnButtonPress}
      >
        {children}
      </AlertModalContent>
    </Modal>
  );
};

export default AlertModal;
